import { useState, useEffect } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import PhotoGrid from '../components/shared/PhotoGrid';
import FullScreenView from '../components/shared/FullScreenView';
import SelectToggleButton from '../components/ad/SelectToggleButton';
import ConfirmModal from '../components/shared/ConfirmModal';
import { useSelection } from '../context/SelectionContext';
import { authHeaders } from '../utils/auth';
import { apiFetch } from '../utils/api';

export default function ArtisticDirectorGallery() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const batchId = searchParams.get('batchId');
  const { adSelectedIds, setAdSelectedIds } = useSelection();

  const [batches, setBatches] = useState([]);
  const [fetchState, setFetchState] = useState({ batchId: null, error: null, photos: [] });
  const [activeIndex, setActiveIndex] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [modal, setModal] = useState(null);
  const showSubmitted = location.state?.submitted === true;

  useEffect(() => {
    if (showSubmitted) window.history.replaceState({}, '');
  }, [showSubmitted]);

  useEffect(() => {
    apiFetch('/api/batches', { headers: authHeaders() })
      .then(r => r.json())
      .then(data => setBatches(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!batchId) return;
    apiFetch(`/api/batches/${batchId}/photos`, { headers: authHeaders() })
      .then(res => {
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        return res.json();
      })
      .then(data => {
        const fetchedPhotos = Array.isArray(data) ? data : data.photos ?? [];
        const initialSelected = new Set(
          fetchedPhotos.filter(p => p.isSelected === true).map(p => p.id)
        );
        setAdSelectedIds(initialSelected);
        setFetchState({ batchId, error: null, photos: fetchedPhotos });
      })
      .catch(err => setFetchState({ batchId, error: err.message, photos: [] }));
  }, [batchId]);

  const toggleSelection = (photoId, newState) => {
    setAdSelectedIds(prev => {
      const next = new Set(prev);
      newState ? next.add(photoId) : next.delete(photoId);
      return next;
    });
  };

  const showConfirm = (opts) => new Promise(resolve => {
    setModal({
      ...opts,
      onConfirm: () => { setModal(null); resolve(true); },
      onCancel:  () => { setModal(null); resolve(false); },
    });
  });

  const handleSubmitSelections = async () => {
    setSubmitError('');
    if (!batchId) { setSubmitError('No batch selected.'); return; }
    if (adSelectedIds.size === 0) { setSubmitError('Select at least one photo before submitting.'); return; }

    const ok = await showConfirm({
      title: 'Submit Photo Selections?',
      body: 'Once submitted, the admin will be notified that the batch is ready to tag.',
      confirmLabel: 'Submit',
    });
    if (!ok) return;

    setSubmitting(true);
    try {
      const res = await apiFetch(`/api/batches/${batchId}/submit-selections`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ selectedPhotoIds: [...adSelectedIds] }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setSubmitError(data.message || 'Failed to submit. Please try again.');
        return;
      }
      navigate(`/review?batchID=${batchId}`, { state: { submitted: true } });
    } catch {
      setSubmitError('Network error — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const loading = batchId !== null && fetchState.batchId !== batchId;
  const { error, photos } = fetchState;
  const activePhoto = activeIndex !== null ? photos[activeIndex] : null;
  const activeBatch = batches.find(b => String(b.id) === batchId);
  const visibleBatches = batches.filter(b => ['uploaded', 'ad_selected'].includes(b.status));

  return (
    <div className='ad-gallery-wrapper'>
      <div className='ad-batch-selector'>
        <label className='ad-batch-label'>Batch:</label>
        <select
          className='ad-batch-select'
          value={batchId || ''}
          onChange={e => {
            const newId = e.target.value;
            setSearchParams(newId ? { batchId: newId } : {});
            setFetchState({ batchId: null, error: null, photos: [] });
            setAdSelectedIds(new Set());
            setActiveIndex(null);
            setSubmitError('');
          }}
        >
          <option value=''>— select a batch —</option>
          {visibleBatches.map(b => (
            <option key={b.id} value={b.id}>
              {b.production} · {b.season} ({b.status})
            </option>
          ))}
        </select>
      </div>

      {activeBatch && (
        <div className='ad-batch-header'>
          <h2 className='ad-batch-title'>{activeBatch.production}</h2>
          <p className='ad-batch-meta'>{activeBatch.season} · {activeBatch.photographer}</p>
        </div>
      )}

      {loading && <p>Loading photos…</p>}
      {error && <p className='ad-error'>Error: {error}</p>}

      {batchId && !loading && !error && photos.length === 0 && (
        <p className='ad-empty-state'>No photos found for this batch.</p>
      )}

      {photos.length > 0 && (
        <PhotoGrid
          photos={photos}
          onSelect={setActiveIndex}
          selectedIds={adSelectedIds}
          columns={4}
          renderOverlay={(photo) => (
            <SelectToggleButton
              photoId={photo.id}
              isSelected={adSelectedIds.has(photo.id)}
              onToggle={toggleSelection}
              compact
            />
          )}
        />
      )}

      {photos.length > 0 && (
        <div className='ad-action-bar'>
              <p className='ad-selection-count'>
                {showSubmitted
                    ? <span style={{ color: 'var(--color-success)', fontWeight: 600 }}>✓ Selections saved.</span>
                    : `${adSelectedIds.size} of ${photos.length} selected for review`
                }
            </p>
            <div>
                {submitError && (
                    <span className='ad-error' style={{ marginRight: 12, fontSize: 'var(--text-sm)' }}>
                        {submitError}
                    </span>
                )}
                <button
                    className='user-submit-btn'
                    onClick={handleSubmitSelections}
                    disabled={submitting || adSelectedIds.size === 0}
                >
                    {submitting ? 'Submitting…' : 'Submit Selections'}
                </button>
            </div>
        </div>
      )}

      <FullScreenView
        photo={activePhoto}
        onClose={() => setActiveIndex(null)}
        onPrev={() => setActiveIndex(i => Math.max(0, i - 1))}
        onNext={() => setActiveIndex(i => Math.min(photos.length - 1, i + 1))}
        hasPrev={activeIndex > 0}
        hasNext={activeIndex !== null && activeIndex < photos.length - 1}
        actionArea={
          activePhoto && (
            <SelectToggleButton
              photoId={activePhoto.id}
              isSelected={adSelectedIds.has(activePhoto.id)}
              onToggle={toggleSelection}
            />
          )
        }
      />

      {modal && <ConfirmModal {...modal} />}
    </div>
  );
}